import React from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import {
  DETECTION_LEVEL_IDS,
  DETECTION_LEVEL_META,
  isDetectionLevelId,
  type DetectionLevelId,
} from '../../constants/detectionPresets';
import { MOTION_LEVEL_META } from '../../constants/motionRiskPresets';

type Variant = 'global' | 'pool' | 'motion' | 'cctvMotion';

type Props = {
  variant: Variant;
  /** '1'…'8' | custom | '' | pool_default(CCTV 시설 기본) */
  value: DetectionLevelId | 'custom' | '' | 'pool_default';
  onSelectLevel: (level: DetectionLevelId) => void;
  onSelectCustom?: () => void;
  /** CCTV 등: 시설 기본(필드 비우기) */
  emptyOptionLabel?: string;
  onSelectEmpty?: () => void;
  layout?: 'radio' | 'select';
  legend?: string;
};

const DetectionLevelPicker: React.FC<Props> = ({
  variant,
  value,
  onSelectLevel,
  onSelectCustom,
  emptyOptionLabel,
  onSelectEmpty,
  layout = 'radio',
  legend,
}) => {
  const descriptionKey =
    variant === 'global'
      ? 'globalDescription'
      : variant === 'motion'
        ? 'motionDescription'
        : variant === 'cctvMotion'
          ? 'motionCctvDescription'
          : 'poolDescription';

  const levelMeta = (id: DetectionLevelId) =>
    variant === 'global' || variant === 'pool'
      ? DETECTION_LEVEL_META[id]
      : MOTION_LEVEL_META[id];

  if (layout === 'select') {
    return (
      <FormControl fullWidth margin="normal">
        {legend ? <FormLabel sx={{ mb: 1 }}>{legend}</FormLabel> : null}
        <Select
          labelId="detection-level-select"
          value={
            value === 'custom'
              ? '__custom__'
              : value === '' || value === 'pool_default'
                ? '__empty__'
                : value
          }
          displayEmpty
          onChange={(e: SelectChangeEvent) => {
            const v = e.target.value;
            if (v === '__custom__') {
              onSelectCustom?.();
              return;
            }
            if (v === '__empty__') {
              onSelectEmpty?.();
              return;
            }
            if (isDetectionLevelId(v)) onSelectLevel(v);
          }}
        >
          {onSelectEmpty && emptyOptionLabel ? (
            <MenuItem value="__empty__">{emptyOptionLabel}</MenuItem>
          ) : null}
          {DETECTION_LEVEL_IDS.map((id) => (
            <MenuItem key={id} value={id}>
              {levelMeta(id).title} — {levelMeta(id)[descriptionKey as keyof ReturnType<typeof levelMeta>]}
            </MenuItem>
          ))}
          {onSelectCustom ? (
            <MenuItem value="__custom__">직접 입력 (고급)</MenuItem>
          ) : null}
        </Select>
        {value === 'custom' && (
          <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 1 }}>
            숫자를 직접 바꾼 상태. 단계를 다시 고르면 값이 일괄 적용됨.
          </Typography>
        )}
      </FormControl>
    );
  }

  return (
    <FormControl component="fieldset" fullWidth>
      {legend ? <FormLabel component="legend">{legend}</FormLabel> : null}
      <RadioGroup
        value={value === 'custom' ? '' : value}
        onChange={(_, v) => {
          if (isDetectionLevelId(v)) onSelectLevel(v);
        }}
      >
        {DETECTION_LEVEL_IDS.map((id) => (
          <FormControlLabel
            key={id}
            value={id}
            control={<Radio size="small" />}
            sx={{ alignItems: 'flex-start', py: 0.25 }}
            label={
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {levelMeta(id).title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {levelMeta(id)[descriptionKey as keyof ReturnType<typeof levelMeta>]}
                </Typography>
              </Box>
            }
          />
        ))}
      </RadioGroup>
      {value === 'custom' && (
        <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 1 }}>
          숫자를 직접 바꾼 상태. 단계를 다시 고르면 아래 값이 일괄 적용됨.
        </Typography>
      )}
    </FormControl>
  );
};

export default DetectionLevelPicker;
